// Die Claude-API im Homelab (internal/ki, docs/ki.md): Guthaben im Monat,
// laufende Repo-Prüfung, Liste der Berichte. GET /v2-api/ki ist
// Boot-Hydration und Abgleich; jede Änderung kommt über den WS-Kanal "ki"
// in derselben Form. Der TEXT eines Berichts steht hinter der PIN und wird
// nur auf seiner Seite geladen (ClaudeBericht.svelte).

import { SvelteMap } from "svelte/reactivity";

import { postJSON, putJSON } from "./api";
import { pinNoetig } from "./claude/chat.svelte";
import { PRUEF_AUFGABE, type KiStand } from "./claude/ki";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

const STORAGE_KEY = "ki";
const ABGLEICH_MS = 120_000;

const _s = new SvelteMap<string, KiStand>();
let _started = false;
let _timer: ReturnType<typeof setInterval> | undefined;
let _unsubscribe: (() => void) | undefined;

function uebernimm(s: KiStand): void {
  if (!s || !Array.isArray(s.berichte)) return;
  _s.set("current", s);
  persist(STORAGE_KEY, s);
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/ki", { credentials: "include" });
    if (!r.ok) return;
    uebernimm((await r.json()) as KiStand);
  } catch {
    // Dienst startet neu -- der nächste Abgleich holt es nach.
  }
}

let _startLauf = 0;

export function startKi(): void {
  if (_started) return;
  _started = true;
  const lauf = ++_startLauf;
  // Darf mitten im Zeichnen gerufen werden (Einstellungen: lies()/info()
  // eines Eintrags). Der gespeicherte Stand kommt darum erst danach in den
  // Store -- sonst bricht Svelte die ganze Seite ab (state_unsafe_mutation;
  // 21.10.2026: die Einstellungen blieben leer, sobald ein Stand im Browser
  // lag).
  queueMicrotask(() => {
    if (lauf !== _startLauf) return; // inzwischen gestoppt (oder neu gestartet)
    registerRevalidator(refresh);
    const cached = loadPersisted<KiStand>(STORAGE_KEY);
    if (cached?.berichte) _s.set("current", cached);
    void refresh();
    _timer = setInterval(refresh, ABGLEICH_MS);
    _unsubscribe = wsSubscribe("ki", (d) => uebernimm(d as KiStand));
  });
}

export function stopKi(): void {
  _startLauf++;
  _started = false;
  if (_timer) clearInterval(_timer);
  _timer = undefined;
  _unsubscribe?.();
  _unsubscribe = undefined;
  _s.clear();
}

export function getKi(): KiStand | undefined {
  return _s.get("current");
}

/** Startet die Repo-Prüfung (die Aufgabe; sie endet, sobald der Container läuft). */
export async function startePruefung(): Promise<void> {
  await postJSON(`/v2-api/tasks/${PRUEF_AUFGABE}/run`, {});
}

/** Monatslimit und/oder Anker fürs Guthaben (Werte aus der Karte der
 *  Console). Hinter der PIN. */
export async function setzeKiEinstellung(a: { grenze_usd?: number; guthaben_usd?: number; guthaben_monat_kosten_usd?: number }): Promise<void> {
  uebernimm(await putJSON<KiStand>("/v2-api/ki/einstellung", a));
}

/** Der Text eines Berichts -- hinter der PIN (er nennt Schwachstellen). */
export class BerichtAnsicht {
  text = $state<string | null>(null);
  gesperrt = $state(false);
  fehler = $state<string | null>(null);

  async lade(datei: string): Promise<void> {
    if (await pinNoetig()) {
      this.gesperrt = true;
      return;
    }
    this.gesperrt = false;
    try {
      const r = await fetch(`/v2-api/ki/berichte/${encodeURIComponent(datei)}`, { credentials: "include" });
      if (r.status === 423) {
        this.gesperrt = true;
        return;
      }
      if (!r.ok) {
        this.fehler = r.status === 404 ? "Diesen Bericht gibt es nicht (mehr)." : `Fehler ${r.status}`;
        return;
      }
      this.text = await r.text();
      this.fehler = null;
    } catch (e) {
      this.fehler = e instanceof Error ? e.message : String(e);
    }
  }
}
